import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Flex, Input, Text } from "@thenounproject/lingo-core";

type Props = {
  open: boolean;
  inspectableCount: number;
  prompts: string[];
  value: string;
  onChange: (value: string) => void;
};

export type ActionsMenuSearchBarRef = {
  focus: () => void;
};

const ActionsMenuSearchBar = React.forwardRef<ActionsMenuSearchBarRef, Props>(
  ({ open, inspectableCount, prompts, value, onChange }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [currentPromptIndex, setCurrentPromptIndex] = useState<number>(0);
    const [placeholder, setPlaceholder] = useState<string>("Actions...");
    const [isAnimating, setIsAnimating] = useState<boolean>(false);

    // Expose focus method to parent, allows focusing the input from outside (ActionsMenu.tsx useEffect with handleKeyDown)
    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus();
      },
    }));

    // Auto-focus when menu opens
    useEffect(() => {
      if (open) {
        inputRef.current?.focus();
      }
    }, [open]);

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value);
      },
      [onChange]
    );

    // Rotate placeholder text every 3 seconds when menu is open and input is empty
    useEffect(() => {
      if (!open || value.length > 0 || prompts.length === 0) {
        setPlaceholder("Actions...");
        setIsAnimating(false);
        return;
      }

      let fadeOutTimeout: NodeJS.Timeout;
      let fadeInTimeout: NodeJS.Timeout;

      const intervalId = setInterval(() => {
        // Clear any pending timeouts from previous iteration
        clearTimeout(fadeOutTimeout);
        clearTimeout(fadeInTimeout);

        setIsAnimating(true);

        // After animation duration, change the text and fade back in
        fadeOutTimeout = setTimeout(() => {
          setCurrentPromptIndex(prevIndex => {
            const nextIndex = (prevIndex + 1) % prompts.length;
            setPlaceholder(prompts[nextIndex]);
            return nextIndex;
          });

          // Remove animation class after text change to fade back in
          fadeInTimeout = setTimeout(() => {
            setIsAnimating(false);
          }, 50);
        }, 300); // Match the CSS transition duration
      }, 3000); // Rotate every 3 seconds

      // Set initial placeholder from prompts
      setPlaceholder(prompts[currentPromptIndex]);

      return () => {
        clearInterval(intervalId);
        clearTimeout(fadeOutTimeout);
        clearTimeout(fadeInTimeout);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, value, prompts]);

    return (
      <Flex
        background="grayLightest"
        p="s"
        flexDirection="column"
        borderBottom="default"
        zIndex={1000}>
        {inspectableCount > 1 && (
          <Text font="ui.small" color="grayDarker" mb="s">
            {inspectableCount} items selected
          </Text>
        )}
        <div style={{ position: "relative" }}>
          <Input
            size="small"
            ref={inputRef}
            placeholder={value ? "" : " "} // Hide default placeholder when we have our custom one
            icon={{ iconId: "action.quick-action", fill: "info" }}
            value={value}
            onChange={handleInputChange}
          />
          {!value && (
            <Text
              font="ui.small"
              position="absolute"
              left="32px" // Account for icon width
              top="50%"
              transform={`translateY(-50%)`}
              style={{ pointerEvents: "none" }}
              color="#9CA3AF" // Gray placeholder color
              transition="all 0.3s ease-in-out"
              opacity={isAnimating ? 0 : 1}>
              {placeholder}
            </Text>
          )}
        </div>
      </Flex>
    );
  }
);

ActionsMenuSearchBar.displayName = "ActionsMenuSearchBar";

export default ActionsMenuSearchBar;
